import React, { useEffect, useState } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Grid,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  DoneAll as DoneAllIcon,
} from "@mui/icons-material";
import { UrlApi } from "../../../../../services/apirest";
import { preparar } from "./functionsFacturacion";

const formatearMoneda = (valor) => {
  if (valor === undefined || valor === null || isNaN(valor)) {
    return "$0,00";
  }
  return `$${valor.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const PasoConfirmacion = ({
  facturaData,
  respuestaFactura,
  cliente,
  pedidosIds,
  loading,
  onBack,
  onSubmit,
  onClose,
  pedidos,
  renglonesCompletos,
}) => {
  const [preFactura, setPreFactura] = useState(null);
  const [calculando, setCalculando] = useState(true);
  const [errorCalculo, setErrorCalculo] = useState(null);
  const [renglonesAgrupados, setRenglonesAgrupados] = useState([]);
  
  const agruparRenglonesPorProducto = (renglones, mapaPrecios) => {
    const agrupados = {};

    renglones.forEach((renglon) => {
      const key = renglon.producto;

      if (!agrupados[key]) {
        agrupados[key] = {
          id: renglon.id,
          uuid_pedido: renglon.uuid_pedido,
          producto: renglon.producto,
          producto_detalle_factura: renglon.nombre_producto,
          cantidad: 0,
          especificaciones: renglon.especificaciones,
          precio: mapaPrecios[renglon.producto] || renglon.precio,
          usuario: renglon.usuario,
          created_at: renglon.created_at,
          updated_at: renglon.updated_at,
          activo: renglon.activo,
          promo: renglon.promo,
          entrega_sena: renglon.entrega_sena,
          valor_envase: renglon.valor_envase,
          iva: renglon.valor_facturacion,
          iva_cod_afip: renglon.iva_cod_afip,
          pedidos_origen: [],
        };
      }

      agrupados[key].cantidad += renglon.cantidad || 0;

      if (
        renglon.id_pedido &&
        !agrupados[key].pedidos_origen.includes(renglon.id_pedido)
      ) {
        agrupados[key].pedidos_origen.push(renglon.id_pedido);
      }
    });

    return Object.values(agrupados);
  };

  useEffect(() => {
    const calcularPreFactura = async () => {
      if (!facturaData?.productos || facturaData.productos.length === 0) {
        setCalculando(false);
        return;
      }

      setCalculando(true);
      setErrorCalculo(null);

      try {
        // 1. Crear mapa de precios desde facturaData
        const mapaPrecios = {};
        facturaData.productos.forEach((prod) => {
          mapaPrecios[prod.id_producto] = prod.precio_unitario;
        });

        // 2. Filtrar renglones por los pedidos seleccionados
        const renglonesFiltrados = renglonesCompletos.filter((renglon) =>
          pedidosIds.includes(renglon.id_pedido)
        );

        // 3. Agrupar renglones
        const agrupados = agruparRenglonesPorProducto(
          renglonesFiltrados,
          mapaPrecios,
        );
        
        setRenglonesAgrupados(agrupados);

        // 4. Preparar el body
        let bodyTemp = {};
        const setBody = (body) => {
          bodyTemp = body;
        };
        

        preparar(
          cliente,
          pedidos,
          1, // suc
          1, // unidad
          setBody,
          () => {},
          agrupados,
        );


        // 5. Llamar al endpoint
        const response = await fetch(
          `${UrlApi}/envasado_pedidos/calcular_facturar_remitos/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(bodyTemp),
          },
        );

        const data = await response.json();

        
        if (!response.ok) {
          throw new Error(data.message || `Error ${response.status}`);
        }

        // ✅ FORMATEAR LOS DATOS PARA QUE COINCIDAN CON LA ESTRUCTURA ESPERADA
        const datosFormateados = {
          success: data.success,
          data: {
            factura: data.data?.factura || data.factura,
            productos: data.data?.productos || data.productos || [],
            totales: {
              subtotal: data.data?.factura?.ImpNeto || data.factura?.ImpNeto || 0,
              iva: data.data?.factura?.ImpIva || data.factura?.ImpIva || 0,
              total: data.data?.factura?.ImpTotal || data.factura?.ImpTotal || 0
            }
          }
        };

        setPreFactura(datosFormateados);
        
      } catch (err) {
        console.error("❌ ERROR:", err);
        setErrorCalculo(err.message);
      } finally {
        setCalculando(false);
      }
    };

    if (renglonesCompletos) {
      calcularPreFactura();
    }
  }, [facturaData, pedidosIds, cliente, pedidos, renglonesCompletos]);

  const renderResultado = () => (
    <Box
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 300,
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          bgcolor: respuestaFactura.color + "20",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
        }}
      >
        {respuestaFactura.title === "Error" ? (
          <ErrorOutlineIcon
            sx={{ fontSize: 40, color: respuestaFactura.color }}
          />
        ) : (
          <DoneAllIcon sx={{ fontSize: 40, color: respuestaFactura.color }} />
        )}
      </Box>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ color: respuestaFactura.color }}
      >
        {respuestaFactura.title}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        align="center"
        sx={{ mb: 2 }}
      >
        {respuestaFactura.message}
      </Typography>
      {respuestaFactura.importe_total && (
        <Typography variant="h4" fontWeight="bold" color="primary.main">
          {formatearMoneda(respuestaFactura.importe_total)}
        </Typography>
      )}
    </Box>
  );

  const renderResumen = () => {
    
    // Determinar qué datos mostrar
    let productosMostrar = [];
    let totalesMostrar = { subtotal: 0, iva: 0, total: 0 };
    
    if (preFactura?.data) {
      // Usar datos de preFactura (del backend)
      productosMostrar = preFactura.data.productos || [];
      totalesMostrar = preFactura.data.totales || {
        subtotal: preFactura.data.factura?.ImpNeto || 0,
        iva: preFactura.data.factura?.ImpIva || 0,
        total: preFactura.data.factura?.ImpTotal || 0
      };
    } else if (facturaData) {
      // Usar datos originales
      productosMostrar = facturaData.productos || [];
      totalesMostrar = facturaData.totales || {
        subtotal: 0,
        iva: 0,
        total: 0
      };
    }


    return (
      <Box>
        {/* Cliente compacto - una sola línea */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: "#f8f9fa" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <Typography variant="body2">
                  <strong>Cliente:</strong> {cliente?.razon_social || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>CUIT:</strong> {cliente?.cuit || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>IVA:</strong> {cliente?.descr_cond_iva || "N/A"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Productos a facturar
        </Typography>
        
        {/* Tabla de productos agrupados */}
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Producto</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  Cantidad
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Precio Unit.
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Subtotal
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  IVA
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Mostrar renglones agrupados */}
              {renglonesAgrupados.length > 0 ? (
                renglonesAgrupados.map((renglon, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {renglon.producto_detalle_factura}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {renglon.pedidos_origen.length} pedido(s)
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {renglon.cantidad}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatearMoneda(renglon.precio)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatearMoneda(renglon.precio * renglon.cantidad / (1 + (renglon.iva/100)))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="info.main">
                        {formatearMoneda(renglon.precio * renglon.cantidad - (renglon.precio * renglon.cantidad / (1 + (renglon.iva/100))))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary.main"
                      >
                        {formatearMoneda(renglon.precio * renglon.cantidad)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No hay productos para mostrar
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 4 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Subtotal:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatearMoneda(totalesMostrar.subtotal)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                IVA:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="info.main">
                {formatearMoneda(totalesMostrar.iva)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Typography variant="body1" fontWeight="bold">
                Total:
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {formatearMoneda(totalesMostrar.total)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Remitos a facturar:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {pedidosIds?.map((id) => {
              const remito = pedidos?.find((p) => p.id === id)?.remito_entrega;
              return (
                <Typography
                  key={id}
                  variant="caption"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    bgcolor: "#e3f2fd",
                    borderRadius: 1,
                    display: "inline-block",
                  }}
                >
                  {remito || "S/N"}
                </Typography>
              );
            })}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <DialogContent dividers>
        {respuestaFactura?.open ? (
          renderResultado()
        ) : calculando ? (
          <Box sx={{ p: 4, textAlign: "center", minHeight: 300 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Calculando pre-factura...</Typography>
          </Box>
        ) : errorCalculo ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorCalculo}
            </Alert>
          </Box>
        ) : (
          renderResumen()
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#f8f9fa",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        {!respuestaFactura?.open ? (
          <>
            <Button
              onClick={onBack}
              disabled={loading || calculando}
              startIcon={<ArrowBackIcon />}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={loading || calculando || !!errorCalculo}
              startIcon={
                loading ? <CircularProgress size={20} /> : <ReceiptIcon />
              }
              color="primary"
            >
              {loading ? "Facturando..." : "Confirmar y Facturar"}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={onClose}
            startIcon={<CloseIcon />}
            sx={{ ml: "auto" }}
          >
            Cerrar
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default PasoConfirmacion;